import classNames from 'classnames';
import { CheckIcon } from 'lucide-react';
import { ChangeEvent, HTMLInputTypeAttribute, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z, ZodError } from 'zod';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { CopyIcon, CopyIconHandle } from '@/Domain/components/icons/CopyIcon';
import { copyToClipboard } from '@/Domain/utils/copyToClipboard';

const COPY_RESET_MS = 1500;

type Props = {
  label: string;
  defaultValue: string | null;
  onChange: (value: string) => void;
  validationSchema?: z.ZodType;
  type?: HTMLInputTypeAttribute;
  copyTestId?: string;
};

export const EditableItem = ({
  label,
  defaultValue,
  onChange,
  validationSchema,
  type,
  copyTestId,
}: Props) => {
  const { t } = useTranslation('inbox_v2');
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyIconRef = useRef<CopyIconHandle>(null);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setInputValue(event.target.value);

    if (validationSchema && event.target.value.toString().trim() !== '') {
      try {
        validationSchema.parse(event.target.value);
        setError(null);
      } catch (err) {
        if (err instanceof ZodError) {
          setError(err.errors[0].message);
          return;
        }
      }
    }

    setError(null);
    onChange(event.target.value);
  };

  const handleCopy = () => {
    copyToClipboard(inputValue, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_RESET_MS);
    });
  };

  useEffect(() => {
    setInputValue(defaultValue || '');
  }, [defaultValue]);

  return (
    <TooltipProvider>
      <Tooltip open={error ? undefined : false}>
        <section className="grid grid-cols-2 items-center leading-5 tracking-wide gap-x-0">
          <p className="text-sm text-muted-foreground">{label}</p>
          {/* asChild keeps the input out of the trigger's button: an <input> nested in a
              <button> is invalid markup and swallows the field's own pointer handling. */}
          <TooltipTrigger asChild>
            <Input
              size="xs"
              type={type || 'text'}
              data-1p-ignore="true"
              value={inputValue}
              onChange={handleOnChange}
              placeholder="+ Add"
              error={!!error}
              endIcon={
                inputValue ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <IconButton
                        variant="ghost"
                        size="xxs"
                        type="button"
                        aria-label={t('inbox_v2:tooltip.copy')}
                        data-testid={copyTestId}
                        onClick={handleCopy}
                        onMouseEnter={() => copyIconRef.current?.startAnimation()}
                        onMouseLeave={() => copyIconRef.current?.stopAnimation()}
                        className={copied ? 'pointer-events-none' : ''}
                      >
                        {copied ? (
                          <CheckIcon size={12} />
                        ) : (
                          <CopyIcon ref={copyIconRef} size={12} />
                        )}
                      </IconButton>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {copied ? t('inbox_v2:tooltip.copied') : t('inbox_v2:tooltip.copy')}
                    </TooltipContent>
                  </Tooltip>
                ) : undefined
              }
              className={classNames(
                'ml-[-8px] rounded-md border text-sm transition duration-200 ease-in-out focus:outline-none focus-within:!border-foreground focus-within:ring-2 focus-within:ring-ring/25 hover:!border-foreground-subtle [&>input]:!pr-9',
                {
                  '!border-destructive hover:!border-destructive': !!error,
                  '!border-transparent': !error,
                },
              )}
            />
          </TooltipTrigger>
        </section>
        <TooltipContent>{error}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
